import {
  RequirementWorkflowType,
  WorkerRequirementStatus,
} from "@clipboard-health/contract-worker-app-bff";
import { useGetRunBackgroundCheckOnBookingConfiguration } from "@src/appV2/redesign/BackgroundChecks/useGetRunBackgroundCheckOnBookingConfiguration";
import { Tab, Tabs } from "@src/appV2/redesign/system/ui-components";
import { useEffect, useRef } from "react";

import { type PillTabOption } from "./types";
import { useDocumentsPageApiContext } from "./useApiProvider";
import { useDocumentsPageNavigationContext } from "./useNavigation";

export function DocumentsPageTabNavigation() {
  const { selectedTab, setSelectedTab } = useDocumentsPageNavigationContext();
  const {
    mandatoryRequirementsQuery: { data: mandatoryRequirements },
  } = useDocumentsPageApiContext();

  const { isEnabled: isRunBackgroundCheckOnBookingEnabled } =
    useGetRunBackgroundCheckOnBookingConfiguration();

  const numberOfIncompleteMandatoryDocuments = mandatoryRequirements
    ?.filter((requirement) => requirement.status !== WorkerRequirementStatus.APPROVED)
    .filter((requirement) => {
      /**
       * ACT-3188: When the run-on-booking flag is ON, we map EXPIRING to APPROVED for Checkr background checks.
       * So we need to filter out the APPROVED and EXPIRING statuses for Checkr background checks.
       */
      if (
        isRunBackgroundCheckOnBookingEnabled &&
        requirement.requirement?.attributes?.requirementWorkflowType ===
          RequirementWorkflowType.CHECKR_BACKGROUND_CHECK
      ) {
        return (
          requirement.status !== WorkerRequirementStatus.EXPIRING &&
          requirement.status !== WorkerRequirementStatus.APPROVED
        );
      }

      return true;
    }).length;

  // Use a ref to ensure we only auto-switch once
  const autoSwitchedRef = useRef(false);

  // Auto switch to workplace specific tab if all mandatory documents are completed
  useEffect(() => {
    if (autoSwitchedRef.current) {
      return;
    }

    autoSwitchedRef.current = true;

    if (selectedTab === "mandatory" && numberOfIncompleteMandatoryDocuments === 0) {
      autoSwitchedRef.current = true;
      setSelectedTab("workplaceSpecific");
    }
  }, [numberOfIncompleteMandatoryDocuments, selectedTab, setSelectedTab]);

  return (
    <Tabs
      variant="pill"
      value={selectedTab}
      sx={{
        px: 5,
      }}
      onChange={(_, value: PillTabOption) => {
        setSelectedTab(value);
      }}
    >
      <Tab
        variant="pill"
        label="Mandatory"
        value="mandatory"
        count={numberOfIncompleteMandatoryDocuments}
      />
      <Tab variant="pill" label="Workplace Specific" value="workplaceSpecific" />
      {/* Workplaces tab will be implemented in a later milestone */}
      {/* <Tab variant="pill" label={PILL_TABS.WORKPLACES.label} value={PILL_TABS.WORKPLACES.value} /> */}
    </Tabs>
  );
}
